import { calendarDaysBetween } from './dates';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isIsoCalendarDate(value: string): boolean {
  if (!DATE_RE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** Compute a booking's full/half-day split and total from its stored rate
    snapshots. Returns null instead of throwing when half days exist without a
    half-day rate so a future UI can render a recoverable "rate missing" state.
    Malformed, duplicate, and out-of-range stored dates are ignored
    defensively; booking routes prevent those values on new writes. */
export function bookingCostCents(booking: {
  start_date: string;
  end_date: string;
  day_rate_cents: number;
  half_day_rate_cents: number | null;
  half_day_dates: string | null;
}): { full_days: number; half_days: number; total_cents: number } | null {
  const totalDays = Math.max(0, calendarDaysBetween(booking.start_date, booking.end_date) + 1);
  const parsed = parseHalfDayDates(booking.half_day_dates);
  const halfDays = new Set(
    parsed.filter(
      (date) => isIsoCalendarDate(date) && date >= booking.start_date && date <= booking.end_date,
    ),
  ).size;

  if (halfDays > 0 && booking.half_day_rate_cents === null) return null;

  const fullDays = Math.max(0, totalDays - halfDays);
  return {
    full_days: fullDays,
    half_days: halfDays,
    total_cents:
      fullDays * booking.day_rate_cents + halfDays * (booking.half_day_rate_cents ?? 0),
  };
}

function parseHalfDayDates(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
}
